/*
 * CAPTURE-OWNED MODULE
 * Confirmation strip for placing a field from one box (a clicked word or a
 * dragged box). The server has read the label next to the value
 * (POST .../zones/resolve-label); nothing is written until Use, which creates
 * the value zone and its anchor zone together (POST .../fields/:id/one-box).
 *
 * When no label is found (a refusal a typed label can rescue) the field can
 * instead be placed by position only: the value zone alone, read where it was
 * drawn (POST .../one-box with position_only). The server allows it only for
 * those refusals and refuses a box at or below the line items table.
 *
 * Presentational: TemplateEditorPage owns the state and the calls. Shown in
 * place of the Place Zone banner, above the viewer, so the document stays
 * usable (clicking another word reads that one instead).
 */
import React from 'react';
import './OneBoxConfirm.css';

/** Below this the label is flagged and the user asked to check it. */
export const LOW_CONFIDENCE = 0.75;

/** Refusals that end the attempt: the message only, then Cancel or another click. */
export const TERMINAL_CODES = new Set([
  'table_cell', 'no_words_under_box', 'box_out_of_page', 'no_sample_document', 'field_not_on_template',
]);

/** Refusals where a typed label can still be used (the server accepts it). */
export const TYPE_TO_RESCUE_CODES = new Set(['no_label_found', 'label_not_found_again']);

export const POSITION_ONLY_OPTION = 'No label for this field (position only)';
export const POSITION_ONLY_HELP = 'Read from this spot on the page. Use only for text that never moves, such as a letterhead.';

const KIND_TEXT = {
  left: 'left of the value',
  inline: 'in the same text as the value',
  above: 'above the value',
  above_block: 'heading of the block',
  below: 'under the value',
  typed: 'typed',
};

export default function OneBoxConfirm({
  fieldLabel,
  status, // 'resolving' | 'ready' | 'refused' | 'saving'
  preview, // resolve-label answer, or null
  error, // { code, message } or null
  choice, // an alternative's text, or null for the best label
  typed,
  noLabel = false, // the label read was refused with a code a typed label can rescue
  positionOnly = false, // place the value zone alone, with no label
  replacing, // how many of the field's zones Use replaces
  onChoose,
  onTypedChange,
  onPositionOnlyChange,
  onUse,
  onCancel,
}) {
  const busy = status === 'resolving' || status === 'saving';
  const terminal = !!error && TERMINAL_CODES.has(error.code);
  const showTyping = !terminal && (!!preview || noLabel || (error && (TYPE_TO_RESCUE_CODES.has(error.code) || error.code === 'label_rejected')));
  const showPositionOnly = !terminal && !preview && noLabel && !!onPositionOnlyChange;
  const hasLabel = !!preview || typed.trim() !== '' || (showPositionOnly && positionOnly);
  const lowConfidence = preview && preview.confidence < LOW_CONFIDENCE;
  const candidates = preview ? [{ ...preview.label, best: true }, ...(preview.alternatives || [])] : [];
  const selected = typed.trim() ? null : (choice ?? preview?.label?.text ?? null);

  return (
    <div className="one-box-confirm" role="dialog" aria-label={`Confirm the label for ${fieldLabel}`}>
      <div className="one-box-confirm__body">
        <div className="one-box-confirm__title">
          Placing <strong>{fieldLabel}</strong>
          {status === 'resolving' && <span className="one-box-confirm__muted">: reading the label next to the value…</span>}
          {status === 'saving' && <span className="one-box-confirm__muted">: saving…</span>}
        </div>

        {preview && (
          <>
            <div className="one-box-confirm__row">
              <span className="one-box-confirm__key">Value</span>
              <span className="one-box-confirm__value" data-testid="one-box-value">{preview.value?.text}</span>
            </div>
            <div className="one-box-confirm__row">
              <span className="one-box-confirm__key">Label</span>
              <span className="one-box-confirm__choices">
                {candidates.map((c) => (
                  <button
                    key={`${c.kind}:${c.text}`}
                    type="button"
                    className={`one-box-confirm__choice${selected === c.text ? ' is-selected' : ''}`}
                    aria-pressed={selected === c.text}
                    title={c.best ? 'Best label next to the value' : c.rank_reason}
                    disabled={busy}
                    onClick={() => onChoose(c.best ? null : c.text)}
                  >
                    {c.text} <span className="one-box-confirm__kind">({KIND_TEXT[c.kind] || c.kind})</span>
                  </button>
                ))}
              </span>
            </div>
            <div className={`one-box-confirm__confidence${lowConfidence ? ' is-low' : ''}`} data-testid="one-box-confidence">
              Confidence {Math.round(preview.confidence * 100)}%
              {lowConfidence && '. Low: check that this is the label for this field, or choose another.'}
            </div>
          </>
        )}

        {error && (
          <div className="one-box-confirm__error" role="alert">{error.message}</div>
        )}

        {showTyping && (
          <label className="one-box-confirm__type">
            <span className="one-box-confirm__key">Type the label</span>
            <input
              type="text"
              value={typed}
              disabled={busy || (showPositionOnly && positionOnly)}
              placeholder="as printed on the document"
              onChange={(e) => onTypedChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && hasLabel && !busy) onUse(); }}
            />
          </label>
        )}

        {showPositionOnly && (
          <label className="one-box-confirm__position-only">
            <input
              type="checkbox"
              checked={positionOnly}
              disabled={busy}
              onChange={(e) => onPositionOnlyChange(e.target.checked)}
            />
            <span>{POSITION_ONLY_OPTION}</span>
          </label>
        )}
        {showPositionOnly && positionOnly && (
          <div className="one-box-confirm__muted" data-testid="one-box-position-only-help">{POSITION_ONLY_HELP}</div>
        )}

        {replacing > 0 && !terminal && (
          <div className="one-box-confirm__muted">
            Use replaces this field&apos;s current {replacing === 1 ? 'zone' : `${replacing} zones`}.
          </div>
        )}
        {!busy && <div className="one-box-confirm__muted">Click another word, or drag a box, to read a different value.</div>}
      </div>

      <div className="one-box-confirm__actions">
        {!terminal && (
          <button type="button" className="btn btn-sm btn-primary" onClick={onUse} disabled={busy || !hasLabel}>
            Use
          </button>
        )}
        <button type="button" className="btn btn-sm btn-cancel-place" onClick={onCancel} disabled={status === 'saving'}>
          Cancel
        </button>
      </div>
    </div>
  );
}
